import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Mail, MessageSquare, Trash2, CheckCircle, Clock, Send, Sparkles, AlertTriangle, Ban, Users } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { useModal } from "../../hooks/useModal";
import { usePanels } from "../../contexts/PanelContext.jsx";

/**
 * Mail Pazarlama — kampanya listesi. Anketler/Toplantılar ile aynı kalıp:
 * TabPage + kart ızgarası, üst araç çubuğu LIST_TOOLBAR_*, silme ConfirmModal.
 */
const STATUS: Record<string, { label: string; cls: string; icon: any }> = {
  DRAFT: { label: "TASLAK", cls: "from-gray-500 to-gray-600", icon: Clock },
  GENERATED: { label: "ONAY BEKLİYOR", cls: "from-amber-500 to-orange-500", icon: Sparkles },
  APPROVED: { label: "KUYRUKTA", cls: "from-blue-500 to-indigo-600", icon: Send },
  SENDING: { label: "GÖNDERİLİYOR", cls: "from-blue-500 to-cyan-600", icon: Send },
  DONE: { label: "TAMAMLANDI", cls: "from-green-500 to-green-600", icon: CheckCircle },
  CANCELLED: { label: "İPTAL", cls: "from-red-500 to-rose-600", icon: Ban },
};
const TYPE_TR: Record<string, string> = { SERVICE: "Hizmet bildirimi", COMMERCIAL: "Ticari ileti" };
const CHANNEL_TR: Record<string, string> = { EMAIL: "E-posta", SMS: "SMS", BOTH: "E-posta + SMS" };

export default function MailMarketingListPage() {
  const navigate = useNavigate();
  const { showToast } = useModal();
  const { mainContentRightInsetActive } = usePanels();
  const canCreate = checkPermission("mail_marketing_create");
  const [smtp, setSmtp] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState<any>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    ApiService.getMailSmtpStatus().then(setSmtp).catch(() => setSmtp(null));
  }, [refreshKey]);

  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "";

  // DynamicList sözleşmesi: { data, meta:{page,totalPages,totalItems,limit} }
  const fetcher = useCallback(async (page: number) => {
    const r = await ApiService.getMailCampaigns(page, 20);
    const limit = r?.limit || 20;
    return { data: r?.items || [], meta: { page: r?.page || page, totalPages: Math.max(1, Math.ceil((r?.total || 0) / limit)), totalItems: r?.total || 0, limit } };
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await ApiService.deleteMailCampaign(selected.id);
      showToast("Kampanya silindi", "success");
      setShowDelete(false); setSelected(null); setRefreshKey((k) => k + 1);
    } catch (e: any) { showToast(e?.message || "Silinemedi", "error"); }
    finally { setDeleting(false); }
  };

  const renderItem = useCallback((c: any) => {
    const st = STATUS[c.status] || STATUS.DRAFT;
    const Icon = st.icon;
    const s = c.stats || {};
    const canDelete = canCreate && c.status !== "SENDING";
    return (
      <div key={c.id} onClick={() => navigate(`/mail-marketing/${c.id}`)}
        className="relative border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col h-full bg-white cursor-pointer">
        {canDelete && (
          <button onClick={(e) => { e.stopPropagation(); setSelected(c); setShowDelete(true); }}
            className="absolute top-4 right-4 z-10 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Sil">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        <div className="flex items-start gap-3 mb-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${c.channel === "SMS" ? "bg-violet-100" : "bg-cyan-100"}`}>
            {c.channel === "SMS" ? <MessageSquare className="h-5 w-5 text-violet-600" /> : <Mail className="h-5 w-5 text-cyan-700" />}
          </div>
          <div className="flex-1 min-w-0 pr-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 break-words">{c.name}</h3>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${st.cls} text-white shadow-sm`}>
                <Icon className="w-3 h-3" />{st.label}
              </span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{TYPE_TR[c.type] || c.type}</span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{CHANNEL_TR[c.channel] || c.channel}</span>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed mb-4">{c.brief}</p>
        <div className="mt-auto pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-[10px] font-medium">{c.creator?.firstName?.charAt(0)}{c.creator?.lastName?.charAt(0)}</span>
            </div>
            <div className="text-xs text-gray-600">
              <div className="font-medium truncate">{c.creator?.firstName} {c.creator?.lastName}</div>
              <div className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(c.createdAt)}</div>
            </div>
            {c.metadata?.audienceCount !== undefined && (
              <div className="ml-auto inline-flex items-center gap-1 text-xs text-gray-600"><Users className="w-3.5 h-3.5" />{c.metadata.audienceCount} kişi</div>
            )}
          </div>
          <div className="grid grid-cols-4 gap-1.5 text-center">
            {[["pending", "Bekleyen", "text-amber-700 bg-amber-50"], ["queued", "Kuyruk", "text-blue-700 bg-blue-50"], ["sent", "Gönderildi", "text-green-700 bg-green-50"], ["failed", "Hata", "text-red-700 bg-red-50"]].map(([k, l, cls]) => (
              <div key={k} className={`rounded-md py-1.5 ${cls}`}>
                <div className="text-sm font-semibold leading-none">{s[k] || 0}</div>
                <div className="text-[10px] mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }, [canCreate, navigate]);

  const actions = useMemo(() => (
    <div className={LIST_TOOLBAR_ROW}>
      {smtp && (
        <span className={`${LIST_TOOLBAR_BTN} cursor-default ${smtp.ok ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
          title={smtp.ok ? `${smtp.from} · bugün ${smtp.sentToday}/${smtp.dailyLimit}` : smtp.message || "SMTP ayarı yok"}
          onClick={() => !smtp.ok && navigate("/settings?tab=smtp")}>
          {smtp.ok ? <CheckCircle className={LIST_TOOLBAR_ICON} /> : <AlertTriangle className={LIST_TOOLBAR_ICON} />}
          <span>{smtp.ok ? `SMTP hazır · ${smtp.sentToday}/${smtp.dailyLimit}` : "SMTP ayarlanmadı"}</span>
        </span>
      )}
      {canCreate && (
        <button onClick={() => navigate("/mail-marketing/new")} className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
          <Plus className={LIST_TOOLBAR_ICON} /><span>Yeni Kampanya</span>
        </button>
      )}
    </div>
  ), [smtp, canCreate, navigate]);

  const tab: TabItemProps = useMemo(() => ({
    type: "list", title: "Tüm Kampanyalar", path: "mail-marketing/campaigns", fetcher, actions,
    isSearch: false, showToggle: true, refreshKey,
    emptyMessage: "Henüz kampanya yok. \"Yeni Kampanya\" ile başla: kitleyi seç, amacı yaz, örnek mesajları gör, onayla.",
    gridClassName: mainContentRightInsetActive
      ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-8 items-stretch auto-rows-fr"
      : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pb-8 items-stretch auto-rows-fr",
    item: renderItem,
  }), [fetcher, actions, refreshKey, renderItem, mainContentRightInsetActive]);

  const tabs = useMemo(() => [tab], [tab]);

  return (
    <>
      <TabPage title="Mail Pazarlama" tabs={tabs} />
      <ConfirmModal isOpen={showDelete} onClose={() => { setShowDelete(false); setSelected(null); }} onConfirm={handleDeleteConfirm}
        title="Kampanyayı Sil" message={`"${selected?.name}" kampanyası ve üretilmiş mesajları silinecek. Bu işlem geri alınamaz.`}
        confirmText="Sil" isLoading={deleting} />
    </>
  );
}
